import { Notification } from "../models/general"; 
import i18n from "../i18n"; 
import moment from "moment";
// Import the new date formatter for centralized configuration
import { DateFormatter } from './dateFormatter';
import { formatLocalDateParam } from './apiDate';

export function validateEmail(email: string) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function getMonday(d: Date) {
  d = new Date(d);
  const day = d.getDay();
  const monday = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  return new Date(d.setDate(monday));
}

export function getSunday(d: Date) {
  d = new Date(d);
  const day = d.getDay();
  const sunday = d.getDate() - day; // adjust when day is sunday
  return new Date(d.setDate(sunday));
}

export function getInitialDay(d: Date, isGCC: boolean = false) {
    return isGCC ? getSunday(d) : getMonday(d)
}

export function getYearMonthPickerFromMonth() {
  const yearMonthPickerFromMonth = new Date();
  yearMonthPickerFromMonth.setMonth(0);
  yearMonthPickerFromMonth.setFullYear(new Date().getFullYear() - 8);
  return yearMonthPickerFromMonth;
}

export function getYearMonthPickerToMonth() {
  const yearMonthPickerToMonth = new Date();
  yearMonthPickerToMonth.setMonth(12);
  yearMonthPickerToMonth.setFullYear(new Date().getFullYear() + 1);
  return yearMonthPickerToMonth;
}

export function getFormattedDuration(seconds: number) {
  const secs = (seconds) % 60 | 0;
  const minutes = (seconds / 60) % 60 | 0;
  const hours = (seconds / 3600) | 0;
  return `${hours.toString().padStart(2, "0")}`
    + `:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function getDurationFormatedAgo(duration: string) {
  const res : any = moment.duration(duration).humanize();

  return res.toString();
}

export function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
  const key = columnKey as keyof T;
  return items.slice(0).sort((a: T, b: T) => {

    if (typeof (b[key]) === "undefined" || b[key] === null) {
      return !isSortedDescending ? 1 : -1;
    } if (typeof (a[key]) === "undefined" || a[key] === null) {
      return !isSortedDescending ? -1 : 1;
    }

    if (typeof (a[key]) === "string" && typeof (b[key]) === "string") {
      return !isSortedDescending
        ? (a[key] as unknown as string).localeCompare(b[key] as unknown as string)
        : (b[key] as unknown as string).localeCompare(a[key] as unknown as string);
    }
    return ((isSortedDescending
      ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1);
  });
}

export function isNullOrWhiteSpace(str?: string) {
  if (str) {
    str = str.replace(/^\s+/, '').replace(/\s+$/, '');
    return str === '';
  }
  return true;
}


export function createUrl(notification: Notification) {
  const url = notification.notificationUrl;
  if (!url)
    return undefined

  // Full URLs: extract the pathname + search for client-side navigation
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      return parsed.pathname + parsed.search;
    } catch {
      return undefined;
    }
  }

  // Relative paths (may include query params like ?tab=onboarding)
  if (url.startsWith("/")) {
    return url;
  }

  return undefined;
}

// ref: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
export function shadeBlend(p: any,c0: any,c1: any) {
  const n=p<0?p*-1:p,u=Math.round,w=parseInt;
  let f: any,t: any
  if (c0) {
    if(c0.length>7){
        f=c0.split(",")
        t=(c1?c1:p<0?"rgb(0,0,0)":"rgb(255,255,255)").split(",")
        const R=w(f[0].slice(4)),G=w(f[1]),B=w(f[2]);
        return "rgb("+(u((w(t[0].slice(4))-R)*n)+R)+","+(u((w(t[1])-G)*n)+G)+","+(u((w(t[2])-B)*n)+B)+")"
    }else{
        f=w(c0.slice(1),16)
        t=w((c1?c1:p<0?"#000000":"#FFFFFF").slice(1),16)
        const R1=f>>16,G1=f>>8&0x00FF,B1=f&0x0000FF;
         
        return "#"+(0x1000000+(u(((t>>16)-R1)*n)+R1)*0x10000+(u(((t>>8&0x00FF)-G1)*n)+G1)*0x100+(u(((t&0x0000FF)-B1)*n)+B1)).toString(16).slice(1)
    }
  }
}

export function isLocInsideWorkplaces(currentLocation: any, workplaces: any[]) {
  let inside = false;
  workplaces.some(workplace => {
    inside = isMarkerInsidePolygon(currentLocation, workplace.bounds)
    return inside;
  })
  return inside;
}

export function isMarkerInsidePolygon(marker : any, poly: any) {
  const polyPoints = poly;       
  const x = marker.lat, y = marker.lng;

  let inside = false;
  for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
      const xi = polyPoints[i].lat, yi = polyPoints[i].lng;
      const xj = polyPoints[j].lat, yj = polyPoints[j].lng;

      const intersect = ((yi > y) !== (yj > y))
          && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
  }

  return inside;
};

export function lightOrDark(color : any) {

  // Variables for red, green, blue values
  let r, g, b, hsp;
  
  // Check the format of the color, HEX or RGB?
  if (color.match(/^rgb/)) {

      // If RGB --> store the red, green, blue values in separate variables
      color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);
      
      r = color[1];
      g = color[2];
      b = color[3];
  } 
  else {
      
      // If hex --> Convert it to RGB: http://gist.github.com/983661
      color = +("0x" + color.slice(1).replace( 
      color.length < 5 && /./g, '$&$&'));

      r = color >> 16;
       
      g = color >> 8 & 255;
      b = color & 255;
  }
  
  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  hsp = Math.sqrt(
  0.299 * (r * r) +
  0.587 * (g * g) +
  0.114 * (b * b)
  );

  // Using the HSP value, determine whether the color is light or dark
  if (hsp>127.5) {

      return 'light';
  } 
  else {

      return 'dark';
  }
}

// Color helpers for dynamic theming
type HSL = { h: number; s: number; l: number };

function clamp(n: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, n));
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const full = hex.replace('#', '');
  const value = full.length === 3
    ? full.split('').map((c) => c + c).join('')
    : full;
  const num = parseInt(value, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

export function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360; s /= 100; l /= 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}

export function hslToHex(h: number, s: number, l: number): string {
  const { r, g, b } = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

export function getContrastText(bgHex: string): '#000000' | '#ffffff' {
  return lightOrDark(bgHex) === 'light' ? '#000000' : '#ffffff';
}

export type GeneratedPalette = {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  accent: string;
  accentDark: string;
  accentLight: string;
  onPrimary: string;
  // Optional extended tokens (background / secondary) for future theming
  secondary?: string;
  bgLight?: string;
  textDark?: string;
  textLight?: string;

  error?: string;
  warning?: string;
  info?: string;
};

export function generatePalette(primaryHex: string): GeneratedPalette {
  const primary = primaryHex || '#304EA1';

  const DEFAULT_PRIMARY = '#304EA1';
  // If using the canonical brand primary, lock variants to spec values
  if (primary.toLowerCase() === DEFAULT_PRIMARY.toLowerCase()) {
    const accent = '#4ADDCB';
    return {
      primary: DEFAULT_PRIMARY,              // Cobalt blue
      primaryDark: '#243A78',                // Spec dark
      primaryLight: '#4A5A8A',               // Spec light
      accent,                                // Turquoise
      accentDark: '#17675c',                 // Spec accent dark
      accentLight: '#7FEFE4',                // Spec accent light
      onPrimary: getContrastText(DEFAULT_PRIMARY),
      secondary: '#F6EFEA',                  // Linen
      bgLight: '#F5F8FC',                    // Cloud white
      textDark: '#243A78',                   // Spec text dark
      textLight: '#4A5A8A',                  // Spec text light

      error: '#D32F2F',                      // Error red
      warning: '#FFA000',                    // Warning amber
      info: '#1976D2',                       // Info blue
    };
  }

  // Dynamic derivation for other colors (fallback logic)
  const primaryDark = shadeBlend(-0.2, primary, undefined) as string;
  const primaryLight = shadeBlend(0.25, primary, undefined) as string;

  // Derive accent: shift hue toward teal range (170‑175) instead of yellow
  const { r, g, b } = hexToRgb(primary);
  const hsl = rgbToHsl(r, g, b);
  // Compute delta to move hue into ~170 range while preserving relative distance
  const targetHue = 172; // turquoise midpoint
  const baseHue = hsl.h;
  const accentHue = ((targetHue - baseHue + 360) % 360 + baseHue + 360) % 360; // not directly used; we'll just set to target
  const accent = hslToHex(targetHue, clamp(hsl.s * 0.85), 55);
  const accentDark = shadeBlend(-0.45, accent, undefined) as string;
  const accentLight = shadeBlend(0.35, accent, undefined) as string;
  return {
    primary,
    primaryDark,
    primaryLight,
    accent,
    accentDark,
    accentLight,
    onPrimary: getContrastText(primary),
    secondary: '#F6EFEA',
    bgLight: '#F5F8FC',
    textDark: primaryDark,
    textLight: shadeBlend(0.35, primary, undefined) as string,
  };
}

export function setCSSColorVariables(palette: GeneratedPalette) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--mowazaf-primary', palette.primary);
  root.style.setProperty('--mowazaf-primary-dark', palette.primaryDark);
  root.style.setProperty('--mowazaf-primary-light', palette.primaryLight);
  // Convenience tokens
  root.style.setProperty('--mowazaf-primary-hover', palette.primaryDark);
  root.style.setProperty('--mowazaf-primary-contrast', palette.onPrimary);
  // Keep the Tailwind `primary-foreground` token (CTA/button text painted on a primary fill) in lockstep
  // with the luminance-computed on-primary contrast. Without this it stays a static white (tokens.css),
  // so a LIGHT tenant brand color renders white-on-light CTA text — a WCAG-AA failure. Dark tenant colors
  // resolve to white here, so existing tenants are unaffected; only light brand colors flip to dark text.
  root.style.setProperty('--color-primary-foreground', palette.onPrimary);
  const primary50 = shadeBlend(0.82, palette.primary, undefined) as string;
  root.style.setProperty('--mowazaf-primary-50', primary50);
  root.style.setProperty('--mowazaf-accent', palette.accent);
  root.style.setProperty('--mowazaf-accent-dark', palette.accentDark);
  root.style.setProperty('--mowazaf-accent-light', palette.accentLight);
  // Text colors derived for readability
  const textDark = palette.primaryDark;
  const textLight = shadeBlend(0.4, palette.primary, undefined) as string;
  root.style.setProperty('--mowazaf-text-dark', textDark);
  root.style.setProperty('--mowazaf-text-light', textLight);
  // Gradients
  root.style.setProperty('--mowazaf-gradient-primary', `linear-gradient(135deg, ${palette.primary} 0%, ${palette.accent} 100%)`);
  root.style.setProperty('--mowazaf-gradient-accent', `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`);
  // Legacy fallback
  root.style.setProperty('--color-primary', palette.primary);

  // New generic token aliases (requested naming) for easier authoring
  root.style.setProperty('--primary', palette.primary);
  root.style.setProperty('--primary-dark', palette.primaryDark);
  root.style.setProperty('--primary-light', palette.primaryLight);
  root.style.setProperty('--accent', palette.accent);
  root.style.setProperty('--accent-dark', palette.accentDark);
  root.style.setProperty('--accent-light', palette.accentLight);
  if (palette.secondary) root.style.setProperty('--secondary', palette.secondary);
  if (palette.bgLight) root.style.setProperty('--bg-light', palette.bgLight);
  root.style.setProperty('--text-dark', palette.textDark || textDark);
  root.style.setProperty('--text-light', palette.textLight || textLight);
  root.style.setProperty('--white', '#ffffff');
  root.style.setProperty('--border-light', 'rgba(255, 255, 255, 0.1)');
  root.style.setProperty('--shadow', '0 10px 30px -5px rgba(0, 0, 0, 0.3)');
  root.style.setProperty('--shadow-soft', '0 10px 40px -15px rgba(48, 78, 161, 0.25)');
  root.style.setProperty('--gradient', `linear-gradient(135deg, ${palette.primary} 0%, ${palette.accent} 100%)`);
  root.style.setProperty('--gradient-accent', `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accentDark} 100%)`);
  root.style.setProperty('--blur-bg', 'rgba(255, 255, 255, 0.8)');
}
export const formatNumber = (number: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

// Legacy date formatting functions - now use centralized configuration
// These are kept for backward compatibility but use the new DateFormatter internally
export const formatDate = (date?: Date): string => {
  if (!date) return '';
  // First try the new centralized formatter
  try {
    return DateFormatter.formatDate(date, 'display');
  } catch (error) {
    // Fallback to the old Intl-based approach for compatibility
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }
}

export const formatDateTime = (date?: Date): string => {
  if (!date) return '';
  // First try the new centralized formatter
  try {
    return DateFormatter.formatDateTime(date, 'display');
  } catch (error) {
    // Fallback to the old Intl-based approach for compatibility
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).format(date);
  }
}

export const formatDuration = (duration: string): string => {
  const [hours, minutes, seconds] = duration.split(':');
  const [secs, millis] = seconds.split('.');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${secs.padStart(2, '0')}`;
};

export const formatDateForInput = (date?: Date): string => {
  if (!date) return '';
  return formatLocalDateParam(date);
}

export const getEnumKeyByValue = (enumerated : any, value: string) => {
  return Object.keys(enumerated)[
    Object.values(enumerated).indexOf(value as typeof enumerated)
  ];
};

export const getEnumValueByKey = <T extends Record<string, string | number>>(
  enumerated: T,
  key: keyof T | string
): T[keyof T] | undefined => {
  if (key in enumerated) {
    return enumerated[key as keyof T];
  }
  return undefined;
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export * from './apiErrors';